// Google Play Billing in der Kunden-App (Auftrag 3, Block 6): Die Kunden-App ist eine TWA (Trusted Web
// Activity) um scan.umzugsscan.app. Dort gilt die Zahlungsrichtlinie von Google: Die 1,99 € für die
// Auswertung laufen über Google Play, nicht über Stripe. Im normalen Browser bleibt Stripe.
//
// Technik: Digital Goods API (Produktpreise, Verbrauchen) und Payment Request API (Kauf), beides nur in
// einer TWA mit eingeschaltetem Play Billing vorhanden (twa-manifest.json: features.playBilling).
//
// Der Kauf läuft so: kaufen → Token an den Server (prüft bei Google, schaltet frei, bestätigt) →
// Kauf abschließen → verbrauchen (consume), damit die nächste Aufnahme wieder gekauft werden kann.

const BILLING = "https://play.google.com/billing";

type Detail = { itemId: string; title: string; price: { currency: string; value: string } };
type Purchase = { itemId: string; purchaseToken: string };
export type DigitalGoods = {
  getDetails(itemIds: string[]): Promise<Detail[]>;
  listPurchases(): Promise<Purchase[]>;
  consume(purchaseToken: string): Promise<void>;
};
type WithDigitalGoods = { getDigitalGoodsService?: (provider: string) => Promise<DigitalGoods> };

/** Läuft die Seite in der Play-Store-App? Der Android-App-Referrer steht nur beim Start da, wir merken ihn uns. */
export function inPlayApp(): boolean {
  const fromApp = document.referrer.startsWith("android-app://");
  try {
    if (fromApp) sessionStorage.setItem("umzugsscan-twa", "1");
    return sessionStorage.getItem("umzugsscan-twa") === "1";
  } catch {
    return fromApp;
  }
}

/** Der Play-Billing-Dienst, oder null (normaler Browser, TWA ohne Billing, ältere Android-Version). */
export async function playDienst(): Promise<DigitalGoods | null> {
  const w = window as unknown as WithDigitalGoods;
  if (typeof w.getDigitalGoodsService !== "function") return null;
  try {
    return await w.getDigitalGoodsService(BILLING);
  } catch {
    return null;
  }
}

/** Preis, wie Google ihn in der Landeswährung anzeigt (z. B. "1,99 €"); null, wenn nicht ermittelbar. */
export async function playPreis(dienst: DigitalGoods, sku: string): Promise<string | null> {
  try {
    const [d] = await dienst.getDetails([sku]);
    if (!d) return null;
    return Number(d.price.value).toLocaleString("de-DE", { style: "currency", currency: d.price.currency });
  } catch {
    return null;
  }
}

/** Noch nicht verbrauchte Käufe dieses Produkts (z. B. nach einem Abbruch zwischen Kauf und Freischaltung). */
export async function offeneKaeufe(dienst: DigitalGoods, sku: string): Promise<string[]> {
  try {
    return (await dienst.listPurchases()).filter((p) => p.itemId === sku).map((p) => p.purchaseToken);
  } catch {
    return [];
  }
}

export class KaufAbgebrochen extends Error {}

/** Kauf auslösen. Gibt den Token und die Abschlussfunktion zurück; wirft KaufAbgebrochen, wenn die Kundin abbricht. */
export async function kaufen(sku: string): Promise<{ token: string; abschliessen: (erfolg: boolean) => Promise<void> }> {
  const request = new PaymentRequest([{ supportedMethods: BILLING, data: { sku } }], {
    // Der Betrag wird bei Play Billing ignoriert (Preis kommt aus der Play Console), das Feld ist aber Pflicht.
    total: { label: "Auswertung einer Wohnungsaufnahme", amount: { currency: "EUR", value: "0" } },
  });
  let response: PaymentResponse;
  try {
    response = await request.show();
  } catch (e) {
    const name = (e as { name?: string }).name;
    if (name === "AbortError" || name === "NotAllowedError") throw new KaufAbgebrochen();
    throw e;
  }
  const token = (response.details as { purchaseToken?: string }).purchaseToken;
  if (!token) {
    await response.complete("fail");
    throw new Error("Kein Kauf-Token von Google Play");
  }
  return { token, abschliessen: (erfolg) => response.complete(erfolg ? "success" : "fail") };
}
